import { INDEX_DB } from "@/constants/general";
import {
  iDbClearTable,
  iDbGetAllRecords,
  iDbUpsertRecord,
} from "@/index-db/db.helpers";
import { ProfileImageCacheType } from "@/index-db/db.types";
import { GERMAN_DATE_FORMAT } from "constants/date-time";
import { apiEndpoints } from "core/api/apiEndpoints";
import dayjs from "dayjs";
import { FieldType, SelectFieldConfig } from "@/models/formField";
const menopauseStageFormOptionFormatter = (itemArray: any) => {
  let returnData = [];
  if (itemArray && itemArray.data && Object.keys(itemArray.data).length > 0) {
    returnData = itemArray.data.menopause_stage.map((item: any) => {
      return {
        value: item.value ? item.value : "",
        label: item.label ? item.label : "",
      };
    });
  }
  return returnData;
};

/**
 * @description creating form config for menopause stage dropdown form
 */
export const MENOPAUSE_STAGE_CONFIG = {
  STAGE_DROPDOWN: {
    type: FieldType.SELECT,
    name: "menopauseStage",
    placeholder: "Select a menopause stage",
    readOnly: false,
    fetch: {
      url: apiEndpoints.getDropdownMasterData,
      method: "get",
      formatter: menopauseStageFormOptionFormatter,
      params: [],
    },
  } as SelectFieldConfig,
};

const checkIfTodayExistsInGermanFormat = (symptoms: any) => {
  // Format today's date in German format, e.g. "17.07.2025"
  const todayGerman = dayjs().startOf("day").format(GERMAN_DATE_FORMAT);

  return symptoms?.some((symptom: any) => {
    // Format the submissionDate to German format for comparison
    const submissionGerman = dayjs(symptom.submissionDate).format(
      GERMAN_DATE_FORMAT,
    );
    return submissionGerman === todayGerman;
  });
};

const checkIfUserAcceptedTrackerDoNotShowPopup = (settings: any) => {
  return settings?.home_page_popup_accepted === 1;
};

const formatProfileApiData = (originalData: any) => {
  const { data } = originalData;

  // Use your existing function to check if symptoms exist for today in German format
  const symptomTrackedToday = checkIfTodayExistsInGermanFormat(data?.symptoms);
  const doNOtShowTrackerPopup = checkIfUserAcceptedTrackerDoNotShowPopup(
    data?.settings,
  );
  // Destructure to remove 'symptoms' and gather the rest of the properties
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { symptoms, settings, ...restOfData } = data;

  // Return the new object without 'symptoms' and with the new boolean field
  return {
    ...restOfData,
    isSymptomTrackedToday: symptomTrackedToday,
    isDoNotShowPopup: doNOtShowTrackerPopup,
  };
};

/**
 * Cache profile image blob to IndexedDB
 * Fetches the image from signed URL, converts to blob, and stores in IndexedDB
 * @param accountId - User's account ID
 * @param signedUrl - Signed URL of the profile image
 * @returns Object URL of the cached blob or the original signed URL as fallback
 */
const cacheProfileImage = async (
  accountId: string,
  signedUrl: string,
): Promise<string> => {
  try {
    const response = await fetch(signedUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    const blob = await response.blob();

    // Validate blob is a valid image before caching
    if (!blob.type || !blob.type.startsWith("image/")) {
      throw new Error(`Invalid image type: ${blob.type || "unknown"}`);
    }

    // Validate blob size (should be greater than 0)
    if (blob.size === 0) {
      throw new Error("Image blob is empty");
    }

    // Only cache if fetch and validation are successful
    const cacheData: ProfileImageCacheType = {
      accountId,
      imageBlob: blob,
      cachedAt: Date.now(),
    };

    await iDbUpsertRecord<ProfileImageCacheType>(
      INDEX_DB.PROFILE_IMAGE_TABLE,
      cacheData,
    );

    const objectUrl = URL.createObjectURL(blob);
    return objectUrl;
  } catch (error) {
    console.error("Failed to cache profile image:", error);
    return "";
  }
};

/**
 * Get cached profile image from IndexedDB
 * @returns Object URL of the cached blob or null if not found
 */
const getCachedProfileImage = async (): Promise<string | null> => {
  try {
    const [cached] = await iDbGetAllRecords<ProfileImageCacheType>(
      INDEX_DB.PROFILE_IMAGE_TABLE,
    );

    if (cached?.imageBlob) {
      return URL.createObjectURL(cached.imageBlob);
    }
    return null;
  } catch (error) {
    console.error("Failed to get cached profile image:", error);
    return null;
  }
};

/**
 * Clear cached profile image from IndexedDB
 */
const clearCachedProfileImage = async (): Promise<void> => {
  try {
    await iDbClearTable(INDEX_DB.PROFILE_IMAGE_TABLE);
  } catch (error) {
    console.error("Failed to clear cached profile image:", error);
  }
};

export {
  cacheProfileImage,
  clearCachedProfileImage,
  formatProfileApiData,
  getCachedProfileImage,
};
